import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiHeadphones,
  FiLayers,
  FiPhoneCall,
  FiZap,
} from "react-icons/fi";
import { useLocalContext } from "../../context/LocalContext";
import "./Hero.css";

const Hero = ({ onSubmitLead }) => {
  const reduceMotion = useReducedMotion();
  const { webinfo = {} } = useLocalContext();

  const brandName = webinfo?.name || "virtuedigihost.com";
  const phoneDisplay = webinfo?.phone || webinfo?.phonecall || "+1 (000) 000-0000";

  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: "",
  });

  const [status, setStatus] = useState({
    loading: false,
    ok: "",
    err: "",
  });

  const services = [
    "Shared or WordPress hosting",
    "VPS / cloud compute",
    "Domains & DNS setup",
    "SSL, email & backups",
    "Migrate from my current host",
    "Not sure — guide me",
  ];

  const trustPoints = [
    "NVMe-friendly stacks, modern PHP, and HTTP/2 where the plan allows",
    "Let’s Encrypt–friendly SSL, backup options, and monitoring add-ons",
    "Real humans for DNS cutovers, email, and migration checklists",
  ];

  const metrics = [
    { value: "99.9%", label: "Uptime target", icon: <FiLayers /> },
    { value: "24/7", label: "Support window", icon: <FiHeadphones /> },
    { value: "Edge", label: "CDN-ready paths", icon: <FiZap /> },
  ];

  const onChange = (e) => {
    setStatus({ loading: false, ok: "", err: "" });
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.phone.trim()) {
      setStatus({
        loading: false,
        ok: "",
        err: "Please enter your name and phone number.",
      });
      return;
    }

    try {
      setStatus({ loading: true, ok: "", err: "" });

      await onSubmitLead?.({
        ...form,
        source: "hosting_hero_lead",
      });

      setStatus({
        loading: false,
        ok: "Thanks — Virtue Digi will reach out shortly.",
        err: "",
      });

      setForm({
        name: "",
        phone: "",
        service: "",
      });
    } catch (error) {
      setStatus({
        loading: false,
        ok: "",
        err: "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <section className="heroPremium">
      <div className="heroPremium-blur heroPremium-blurOne" />
      <div className="heroPremium-blur heroPremium-blurTwo" />

      <div className="heroPremium-wrap">
        <motion.div
          className="heroPremium-left"
          initial={reduceMotion ? false : { opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="heroPremium-badge">
            <span className="heroPremium-badgeDot" />
            Virtue Digi • Cloud hosting • Plain-English support
          </div>

          <h1 className="heroPremium-title">
            Hosting that scales with
            <span>your traffic and your team</span>
          </h1>

          <p className="heroPremium-subtitle">
            {brandName} is the Virtue Digi home for shared, WordPress, and VPS
            plans—clear specs, sensible security defaults, and help when DNS,
            SSL, or a migration feels overwhelming.
          </p>

          <div className="heroPremium-points">
            {trustPoints.map((item) => (
              <div className="heroPremium-point" key={item}>
                <FiCheckCircle />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="heroPremium-actions">
            <Link to="/services" className="heroPremium-btn heroPremium-btnPrimary">
              Compare plans
              <FiArrowRight />
            </Link>

            <a href={`tel:${phoneDisplay}`} className="heroPremium-btn heroPremium-btnGhost">
              <FiPhoneCall />
              Call sales
            </a>
          </div>

          <div className="heroPremium-stats">
            {metrics.map((item) => (
              <div className="heroPremium-statCard" key={item.label}>
                <div className="heroPremium-statIcon">{item.icon}</div>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="heroPremium-right"
          initial={reduceMotion ? false : { opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reduceMotion ? 0 : 0.5,
            delay: reduceMotion ? 0 : 0.1,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <div className="heroPremium-formCard">
            <div className="heroPremium-formTop">
              <p className="heroPremium-formEyebrow">Talk to Virtue Digi</p>
              <h2>Request a hosting plan</h2>
              <p>
                Tell us your domains, traffic band, and CMS—we’ll suggest a tier,
                timeline, and migration path if you’re leaving another host.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="heroPremium-form">
              <div className="heroPremium-field">
                <label htmlFor="name">Your Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="heroPremium-field">
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="heroPremium-field">
                <label htmlFor="service">What are you looking for?</label>
                <select
                  id="service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                >
                  <option value="">Select an option</option>
                  {services.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              {status.err ? <div className="heroPremium-msg heroPremium-msgErr">{status.err}</div> : null}
              {status.ok ? <div className="heroPremium-msg heroPremium-msgOk">{status.ok}</div> : null}

              <button
                type="submit"
                className="heroPremium-submit"
                disabled={status.loading}
              >
                {status.loading ? "Sending..." : "Send request"}
              </button>
            </form>

            <div className="heroPremium-miniTrust">
              <span>No obligation</span>
              <span>Migration help</span>
              <span>Fast response</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
